/**
 * Document Upload Panel for Tenant Onboarding (DRR-073)
 *
 * Allows FERP Operations to upload customer documents into the Chaos
 * ingestion pipeline for classification → config generation → approval.
 *
 * Accepted document types map to onboarding-document-classification config:
 * - Employee handbooks → HR workflows + rules
 * - Compliance manuals → CRITICAL/HIGH safety configs
 * - Business rules → MEDIUM operational workflows
 * - Service agreements → SLA rules
 * - Integration docs → Connector configs
 *
 * FERP Principles:
 * - Data-driven: Accepted types from backend metadata
 * - Repository pattern: Upload via GraphQL mutation
 * - Audit trail: All uploads tracked with user + timestamp
 */

import { useState, useCallback } from 'react';
import {
  Stack,
  Card,
  Text,
  Group,
  Badge,
  Button,
  Alert,
  Table,
  Progress,
  ThemeIcon,
} from '@mantine/core';
import {
  IconUpload,
  IconFile,
  IconFileText,
  IconTrash,
  IconPlayerPlay,
  IconCheck,
  IconClock,
  IconAlertTriangle,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useTriggerChaosPipelineMutation } from '@/generated/graphql';

// ============================================================================
// Types
// ============================================================================

interface StagedDocument {
  file: File;
  documentType: string;
  status: 'staged' | 'uploading' | 'classified' | 'error';
  error?: string;
}

export interface DocumentUploadPanelProps {
  tenantId: string;
  tenantName: string;
}

// ============================================================================
// Document type detection (mirrors onboarding-document-classification config)
// ============================================================================

const DOCUMENT_TYPE_HINTS: Array<{ keywords: string[]; type: string; label: string; safetyHint: string }> = [
  { keywords: ['hipaa', 'phi', 'patient', 'clinical'], type: 'compliance_manual', label: 'HIPAA Compliance', safetyHint: 'CRITICAL' },
  { keywords: ['sox', 'sarbanes', 'financial control', 'segregation'], type: 'compliance_manual', label: 'SOX Controls', safetyHint: 'CRITICAL' },
  { keywords: ['handbook', 'employee', 'conduct', 'termination', 'pto'], type: 'employee_handbook', label: 'Employee Handbook', safetyHint: 'HIGH' },
  { keywords: ['flsa', 'overtime', 'wage', 'labor', 'workers comp'], type: 'compliance_manual', label: 'Labor Law', safetyHint: 'HIGH' },
  { keywords: ['process', 'procedure', 'workflow', 'sop'], type: 'business_rules', label: 'Business Process', safetyHint: 'MEDIUM' },
  { keywords: ['sla', 'service level', 'service agreement'], type: 'service_agreement', label: 'Service Agreement', safetyHint: 'MEDIUM' },
  { keywords: ['api', 'integration', 'webhook', 'endpoint', 'hl7'], type: 'integration_spec', label: 'Integration Spec', safetyHint: 'MEDIUM' },
  { keywords: ['org chart', 'organization', 'department', 'hierarchy'], type: 'org_structure', label: 'Org Structure', safetyHint: 'LOW' },
];

function detectDocumentType(filename: string): { type: string; label: string; safetyHint: string } {
  const lower = filename.toLowerCase();
  for (const hint of DOCUMENT_TYPE_HINTS) {
    if (hint.keywords.some((kw) => lower.includes(kw))) {
      return hint;
    }
  }
  return { type: 'unclassified', label: 'Unclassified', safetyHint: 'HIGH' };
}

const SAFETY_COLORS: Record<string, string> = {
  CRITICAL: 'red',
  HIGH: 'orange',
  MEDIUM: 'yellow',
  LOW: 'green',
};

// DRR-081: Only PDF and plain text formats accepted
const ACCEPTED_EXTENSIONS = ['.pdf', '.txt', '.csv', '.md'];

const ALLOWED_MIME_TYPES = new Set([
  'application/pdf',
  'text/plain',
  'text/csv',
  'text/markdown',
]);

function getFileIcon(filename: string) {
  const ext = filename.split('.').pop()?.toLowerCase() ?? '';
  if (ext === 'pdf') return IconFileText;
  if (['txt', 'csv', 'md'].includes(ext)) return IconFile;
  return IconFile;
}

/** DRR-081: Validate file format and show rejection notification */
function isAllowedFormat(file: File): boolean {
  const ext = `.${file.name.split('.').pop()?.toLowerCase() ?? ''}`;
  if (ACCEPTED_EXTENSIONS.includes(ext) || ALLOWED_MIME_TYPES.has(file.type)) {
    return true;
  }
  notifications.show({
    title: `File format (${ext}) not supported`,
    message:
      'You can: export as PDF from your application, scan the document and upload as an image-based PDF, or use a plain text file (.txt). Accepted formats: PDF, TXT, CSV, MD.',
    color: 'orange',
    autoClose: 8000,
  });
  return false;
}

// ============================================================================
// Component
// ============================================================================

export function DocumentUploadPanel({
  tenantId,
  tenantName,
}: DocumentUploadPanelProps): React.ReactElement {
  const [stagedDocs, setStagedDocs] = useState<StagedDocument[]>([]);
  const [ingestionTriggered, setIngestionTriggered] = useState(false);

  const [triggerIngestion, { loading: ingesting }] = useTriggerChaosPipelineMutation();

  const handleFileSelect = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.accept = ACCEPTED_EXTENSIONS.join(',');
    input.onchange = (e) => {
      const files = (e.target as HTMLInputElement).files;
      if (!files) return;

      // DRR-081: Filter out non-allowed formats (shows notification per rejected file)
      const allowedFiles = Array.from(files).filter(isAllowedFormat);
      if (allowedFiles.length === 0) return;

      const newDocs: StagedDocument[] = allowedFiles.map((file) => {
        const detected = detectDocumentType(file.name);
        return {
          file,
          documentType: detected.type,
          status: 'staged' as const,
        };
      });

      setStagedDocs((prev) => [...prev, ...newDocs]);
    };
    input.click();
  }, []);

  const handleRemoveDoc = useCallback((index: number) => {
    setStagedDocs((prev) => prev.filter((_: any, i) => i !== index));
  }, []);

  const handleTriggerIngestion = useCallback(async () => {
    if (stagedDocs.length === 0) return;

    // Mark all as uploading
    setStagedDocs((prev) =>
      prev.map((d) => ({ ...d, status: 'uploading' as const })),
    );

    try {
      // triggerChaosPipeline accepts one document at a time per TriggerChaosPipelineInput
      for (const doc of stagedDocs) {
        await triggerIngestion({
          variables: {
            input: {
              documentId: doc.file.name,
              tenant: tenantId,
              triggeredBy: 'portal-upload',
              safetyLevel: 'HIGH',
              targetDomain: doc.documentType !== 'unclassified' ? doc.documentType : undefined,
            },
          },
        });
      }

      setStagedDocs((prev) =>
        prev.map((d) => ({ ...d, status: 'classified' as const })),
      );
      setIngestionTriggered(true);
    } catch (err) {
      setStagedDocs((prev) =>
        prev.map((d) => ({
          ...d,
          status: 'error' as const,
          error: err instanceof Error ? err.message : 'Upload failed',
        })),
      );
    }
  }, [stagedDocs, tenantId, triggerIngestion]);

  const totalSize = stagedDocs.reduce((acc, d) => acc + d.file.size, 0);
  const uploadingCount = stagedDocs.filter((d) => d.status === 'uploading').length;

  return (
    <Stack gap="md">
      {/* Upload area */}
      <Card withBorder padding="lg" style={{ textAlign: 'center' }}>
        <Stack ta="center" gap="sm">
          <ThemeIcon size="xl" variant="light" color="blue">
            <IconUpload size={24} />
          </ThemeIcon>
          <Text fw={600}>Upload Customer Documents</Text>
          <Text size="sm" c="dimmed">
            Upload {tenantName}&apos;s business documents for Chaos ingestion.
            Only PDF and plain text formats are accepted (DRR-081).
            You can scan documents as image-based PDFs or export from your application as PDF.
          </Text>
          <Button
            leftSection={<IconUpload size={16} />}
            onClick={handleFileSelect}
            disabled={ingesting}
          >
            Select Files
          </Button>
          <Text size="xs" c="dimmed">
            Accepted: {ACCEPTED_EXTENSIONS.join(', ')}
          </Text>
        </Stack>
      </Card>

      {/* Staged documents table */}
      {stagedDocs.length > 0 && (
        <Card withBorder>
          <Group justify="space-between" mb="md">
            <Text fw={600}>
              Staged Documents ({stagedDocs.length})
            </Text>
            <Text size="sm" c="dimmed">
              {(totalSize / 1024).toFixed(1)} KB total
            </Text>
          </Group>

          {uploadingCount > 0 && (
            <Progress value={65} animated size="sm" mb="md" />
          )}

          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Document</Table.Th>
                <Table.Th>Detected Type</Table.Th>
                <Table.Th>Safety Hint</Table.Th>
                <Table.Th>Size</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {stagedDocs.map((doc, idx) => {
                const detected = detectDocumentType(doc.file.name);
                const FileIcon = getFileIcon(doc.file.name);
                const statusIcon = doc.status === 'classified'
                  ? <IconCheck size={14} color="green" />
                  : doc.status === 'error'
                    ? <IconAlertTriangle size={14} color="red" />
                    : doc.status === 'uploading'
                      ? <IconClock size={14} color="blue" />
                      : null;

                return (
                  <Table.Tr key={`${doc.file.name}-${idx}`}>
                    <Table.Td>
                      <Group gap="xs">
                        <FileIcon size={16} />
                        <Text size="sm">{doc.file.name}</Text>
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Badge size="sm" variant="light">
                        {detected.label}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Badge
                        size="sm"
                        color={SAFETY_COLORS[detected.safetyHint] ?? 'gray'}
                        variant="light"
                      >
                        {detected.safetyHint}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" c="dimmed">
                        {(doc.file.size / 1024).toFixed(1)} KB
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4}>
                        {statusIcon}
                        <Text size="xs">{doc.status}</Text>
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      {doc.status === 'staged' && (
                        <Button
                          size="xs"
                          variant="subtle"
                          color="red"
                          onClick={() => handleRemoveDoc(idx)}
                        >
                          <IconTrash size={14} />
                        </Button>
                      )}
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>

          {/* Trigger ingestion */}
          <Group justify="flex-end" mt="md">
            <Button
              leftSection={<IconPlayerPlay size={16} />}
              color="green"
              loading={ingesting}
              disabled={stagedDocs.length === 0 || ingestionTriggered}
              onClick={handleTriggerIngestion}
            >
              {ingestionTriggered ? 'Ingestion Triggered' : 'Trigger Chaos Ingestion'}
            </Button>
          </Group>
        </Card>
      )}

      {/* Post-ingestion guidance */}
      {ingestionTriggered && (
        <Alert color="blue" title="Ingestion Started" icon={<IconCheck size={16} />}>
          Documents are being processed through the Chaos ingestion pipeline.
          Classification rules will determine safety levels and generate configurations.
          Check the Approval Queue tab for generated configs awaiting review.
        </Alert>
      )}

      {/* Pipeline reference */}
      <Card withBorder>
        <Text fw={600} mb="xs">Classification Pipeline</Text>
        <Text size="sm" c="dimmed" mb="sm">
          Uploaded documents flow through these Zen Engine rule sets:
        </Text>
        <Stack gap={4}>
          <Group gap="xs">
            <Badge size="xs" color="blue" variant="light">1</Badge>
            <Text size="sm">Document Classification — Classify by type + safety level</Text>
          </Group>
          <Group gap="xs">
            <Badge size="xs" color="blue" variant="light">2</Badge>
            <Text size="sm">Config Generation — Determine configurations to generate</Text>
          </Group>
          <Group gap="xs">
            <Badge size="xs" color="blue" variant="light">3</Badge>
            <Text size="sm">Approval Routing — Route to approval workflow</Text>
          </Group>
        </Stack>
      </Card>
    </Stack>
  );
}

export default DocumentUploadPanel;
